import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Clock, Target } from "lucide-react";
import { api } from "../lib/api";

const ACTION_IMG = "https://images.unsplash.com/photo-1554068865-24cecd4e34b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHx0ZW5uaXMlMjBwbGF5ZXIlMjBhY3Rpb258ZW58MHx8fHwxNzg3OTAyNTg5fDA&ixlib=rb-4.1.0&q=85";

export default function Coaching() {
  const [coaches, setCoaches] = useState([]);
  const [programs, setPrograms] = useState([]);
  useEffect(() => {
    api.get("/coaches").then((r) => setCoaches(r.data)).catch(() => {});
    api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="coaching-page">
      <section className="relative overflow-hidden border-b border-white/10">
        <img src={ACTION_IMG} alt="Tennis coach demonstrating a forehand" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-28">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-[#ccff00] uppercase tracking-[0.3em] text-sm mb-4">Coaching</p>
            <h1 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl">
              Coached by people who've <span className="text-[#ccff00]">played for points.</span>
            </h1>
            <p className="text-white/70 max-w-xl mt-6 leading-relaxed">
              Ex-tour professionals and certified academy directors. Every program is built around measurable improvement — not just sweat.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="coaches-section">
        <h2 className="font-display uppercase text-3xl lg:text-5xl tracking-tight mb-14">The coaching team</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {coaches.map((c, i) => (
            <motion.article
              key={c.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              className="bg-[#0f0f10] border border-[#2a2a2a] rounded-md p-8 hover:-translate-y-1 hover:border-[#ccff00]/50 transition-all duration-300 flex flex-col"
              data-testid={`coach-card-${c.id}`}
            >
              <div className="w-20 h-20 rounded-full bg-[#1a1a1a] border-2 border-[#ccff00] flex items-center justify-center font-display text-2xl text-[#ccff00] mb-6">
                {c.initials}
              </div>
              <h3 className="font-display uppercase text-xl tracking-tight">{c.name}</h3>
              <p className="text-[#ccff00] text-xs uppercase tracking-widest mt-1 mb-4">{c.role}</p>
              <p className="text-white/60 text-sm leading-relaxed flex-1">{c.bio}</p>
              <div className="mt-6 pt-6 border-t border-white/10 space-y-2 text-sm">
                <p className="flex items-center gap-2 text-white/70"><Target size={14} className="text-[#ccff00]" /> {c.specialty}</p>
                <p className="flex items-center gap-2 text-white/70"><Clock size={14} className="text-[#ccff00]" /> {c.experience}</p>
                <p className="font-bold text-white mt-2">${c.price}<span className="text-white/50 font-normal">/hr private</span></p>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="border-t border-white/10 bg-[#0f0f10]" data-testid="programs-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <h2 className="font-display uppercase text-3xl lg:text-5xl tracking-tight mb-14">Programs</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {programs.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 2) * 0.12, duration: 0.5 }}
                className="border border-[#2a2a2a] bg-[#0a0a0a] rounded-md p-8 hover:border-[#ccff00]/50 transition-colors duration-300"
                data-testid={`program-card-${p.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-display uppercase text-2xl tracking-tight">{p.name}</h3>
                  <span className="text-[11px] uppercase tracking-widest bg-[#ccff00]/10 text-[#ccff00] border border-[#ccff00]/30 px-3 py-1.5 rounded-full whitespace-nowrap">{p.level}</span>
                </div>
                <p className="text-white/60 mt-4 leading-relaxed">{p.blurb}</p>
                <p className="text-white/40 text-sm mt-6 uppercase tracking-widest">{p.schedule}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
